
import {calculate_rds_config,pick_rds_shape,pick_rac_shape} from "./dbcsi_rds.js";

/**
 * Determines if a group of databases can be consolidated onto a single RDS instance.
 * 
 * Logic:
 * // 1. Enforces maximum group size of 3 databases for manageability
 * 2. Ensures all databases have the same Multi-AZ configuration (cannot mix)
 * 3. When rdsConsolidation_groupbyversion="Y", also ensures same Oracle version
 * 4. Calculates combined resource requirements (CPU, memory, throughput, IOPS, disk)
 * 4. Validates total disk space doesn't exceed the limit (default 64TB)
 * 5. Checks if any existing database's target RDS shape can handle the combined load
 * 6. If basic consolidation fails but rdsConsolidation_algo="Y2" or "Y3", attempts to find
 *    a better RDS shape that could accommodate the combined requirements
 * 
 * @param {Array} dbGroup - Array of database objects to potentially consolidate
 * @param {Object} regionInfo - AWS region information for shape availability
 * @param {Object} prefs - User preferences including consolidation algorithm
 * @param {number} maxDiskspace - Maximum allowed disk space in GB (default: 64000)
 * @returns {boolean} True if databases can be consolidated, false otherwise
 */
function canConsolidate(dbGroup, regionInfo, prefs, maxDiskspace = 64000, verbose = false) {
   // if (dbGroup.length > 3) return false;
    
    // console.log("canConsolidate", dbGroup, verbose);
    
    // If using baremetal algorithm, use specialized function
    if (prefs.rdsConsolidation_algo === "baremetal") {
        return canConsolidateBaremetal(dbGroup, regionInfo, prefs, maxDiskspace);
    }
    
    const multiAzValues = new Set(dbGroup.map(db => db.UseMAZ));
    if (multiAzValues.size > 1) return false;
    
    const totalVcpu = dbGroup.reduce((sum, db) => sum + db.VCPU, 0);
    const totalMemory = dbGroup.reduce((sum, db) => sum + db.Memory, 0);
    const totalThroughput = dbGroup.reduce((sum, db) => sum + db.Throughput, 0);
    const totalIops = dbGroup.reduce((sum, db) => sum + db.IOPS, 0);
    const totalDiskspace = dbGroup.reduce((sum, db) => sum + db.DiskSpace, 0);
    
//    if (verbose) console.log("totals", totalVcpu, totalMemory, totalThroughput);
    
    if (totalDiskspace > maxDiskspace) return false;
    
    var max_single_server_vcpu = 0;
    // Check if any single server can handle all the combined requirements
    for (const db of dbGroup) {
        if (db.targetconfig_rds.shape_max_vcpu*1 > 0 && db.targetconfig_rds.shape_memory*1 > 0 && 
            db.targetconfig_rds.shape_throughput*1 > 0 && db.targetconfig_rds.shape_IOPS*1 > 0) {
              
//              if (verbose) console.log("db",db.row, db.targetconfig_rds.shape_max_vcpu, db.targetconfig_rds.shape_memory,db.targetconfig_rds.shape_throughput );
            
            if (totalVcpu <= db.targetconfig_rds.shape_max_vcpu &&
                totalMemory <= db.targetconfig_rds.shape_memory &&
                totalThroughput <= db.targetconfig_rds.shape_throughput &&
                totalIops <= db.targetconfig_rds.shape_IOPS) {
               // console.log("CanConsolidate TRUE", dbGroup);  
                return true;
            }
        }
        if (db.targetconfig_rds.shape_max_vcpu>max_single_server_vcpu) {
          max_single_server_vcpu=db.targetconfig_rds.shape_max_vcpu;
        }
    }
    
    // if we got here, none of our single servers can not handle it.
    // if algo=Y2...
    // then find the maximum VCPU of any single shape.  if totalVCPU is > this max, then just say false.
    // but if not, we should try looking for a shape with the same VCPU just better specs (more memory or throughput) that could help
    // can use pick_rds_shape (with the totals).  and check to see if what we got back has the same or less VCPU. [note: this might not work well for optgoal=cost]
    if ( ( (prefs.rdsConsolidation_algo=="Y2") && ( (totalVcpu <= max_single_server_vcpu) || (max_single_server_vcpu==2)  )  ) ||
         ( (prefs.rdsConsolidation_algo=="Y3") &&  ( (totalVcpu <= max_single_server_vcpu*2) || (max_single_server_vcpu==2)  ) )
       ) {
    // if ( ( (totalVcpu <= max_single_server_vcpu) || (max_single_server_vcpu==2)  ) 
    //       && ( (prefs.rdsConsolidation_algo=="Y2") || (prefs.rdsConsolidation_algo=="Y3") ) ) {
      // console.log("algo=Y2/Y3: opportunity to look for a better shape with VCPU=", max_single_server_vcpu);
      
        var config = {
            VCPU: totalVcpu,
            Memory: totalMemory,
            Throughput: totalThroughput,
            IOPS: totalIops,
            DiskSpace: totalDiskspace,
            rds_type: prefs.rds_algo,
            optgoal_choice: prefs.optgoal_algo,
            // UseRI: serverGroup[0].UseRI,
            // UseMAZ: serverGroup[0].UseMAZ,
            frozen_prefs: prefs
        };    
        
        var rds_shape = pick_rds_shape(regionInfo, config);
        if (rds_shape.Name != null) {
              
    
//            if (verbose) console.log("algo=Y2/Y3: better shape", rds_shape, config);
            
            // if Y3, then just use better shape.
            // if Y2, then only use shape if it is not greater than biggest server
            if ( (prefs.rdsConsolidation_algo=="Y3") || (rds_shape.VCPU*1 <= max_single_server_vcpu ) ) {
              // found a better shape
              if (verbose) console.log("algo=Y2/Y3: found a better shape", rds_shape.Name, dbGroup);
              return true;
            }
            
            // special case for Y2: if our max_single_server_vcpu is 2 (.large), then it is OK if our new server is 4. (.xlarge)
            if (rds_shape.VCPU*1==4) {
              if (verbose) console.log("algo=Y2: combining .large into .xlarge");
              return true;
            }
        } else {
 //         if (verbose) console.log("algo=Y2/Y3: better shape could not be found", rds_shape, config);
        }
      
    }
    
    
    return false;
}

/**
 * Optimizes database consolidation by grouping databases to minimize server count
 * while respecting resource constraints and Multi-AZ requirements.
 * 
 * Logic:
 * 1. Groups databases by Multi-AZ configuration (cannot consolidate across different MAZ settings)
 * 2. When rdsConsolidation_groupbyversion="Y", also groups by Oracle version within each MAZ group
 * 3. Within each group, sorts databases by consolidation priority:
 *    - Prioritizes databases with largest gaps between provisioned and required CPU
 *    - Secondary sort by actual CPU, memory, and disk space requirements
 * 4. Uses greedy algorithm to build consolidation groups:
 *    - Starts with highest priority database as anchor
 *    - Attempts to add remaining databases (up to 3 per group)
 *    - Uses canConsolidate() to validate each potential grouping
 * 5. Continues until all databases are assigned to consolidation groups
 * 
 * The sorting strategy targets databases that are most over-provisioned first,
 * as these offer the best consolidation opportunities.
 * 
 * @param {Array} databases - Array of database objects to consolidate
 * @param {Object} regionInfo - AWS region information for shape availability
 * @param {Object} prefs - User preferences for consolidation behavior
 * @returns {Array} Array of database groups, where each group can be consolidated
 */
function optimizeConsolidation(databases, regionInfo, prefs) {
    const multiAzGroups = {};
    var maxStorage = 64000;
    if ( (prefs.rds_algo!='custom') && (prefs.asv_min_storage.trim()!='') && (prefs.asv_min_storage*1>0) ) {
        maxStorage = 256000;
    }
    databases.forEach(db => {
        // Create grouping key based on MultiAZ and optionally version
        let groupKey = db.UseMAZ;
        if (prefs.rdsConsolidation_groupbyversion === "Y") {
            groupKey = `${db.UseMAZ}_${db.VERSION}`;
        }
        
        // Add read replica match grouping if readreplica_algo is Y or auto
        if ((prefs.readreplica_algo === "Y" || prefs.readreplica_algo === "auto") && 
            prefs.readreplica_db_name_match_expr && 
            prefs.readreplica_db_name_match_expr.trim() !== '') {
            
            let matches = false;
            try {
                let pattern = prefs.readreplica_db_name_match_expr.trim();
                if (pattern.includes('*') && !pattern.includes('.*')) {
                    pattern = pattern.replace(/\*/g, '.*');
                }
                const regex = new RegExp(pattern);
                matches = regex.test(db.database);
            } catch (e) {
                matches = false;
            }
            groupKey = `${groupKey}_replica_${matches}`;
        }
        
        if (!multiAzGroups[groupKey]) {
            multiAzGroups[groupKey] = [];
        }
        multiAzGroups[groupKey].push(db);
    });
    
    const consolidatedServers = [];
    var combinationsChecked = 0;
    
    Object.values(multiAzGroups).forEach(dbList => {
        // start with the shapes with the biggest gaps between provisioned VCPU and target VCPU
        dbList.sort((a, b) => {
            return ((b.targetconfig_rds.shape_max_vcpu-b.VCPU)*1000000 + b.VCPU*10000 + b.Memory + b.DiskSpace/10000) - 
                   (a.targetconfig_rds.shape_max_vcpu-a.VCPU)*1000000 + (a.VCPU*10000 + a.Memory + a.DiskSpace/10000);
        });
        
        const remainingDbs = [...dbList];
        

        
        
        var firstN = 4;
        
        while (remainingDbs.length > 0) {
          // sort again to biggest VCPU gap first.
          
            remainingDbs.sort((a, b) => {
                return ((b.targetconfig_rds.shape_max_vcpu-b.VCPU)*1000000 + b.VCPU*10000 + b.Memory + b.DiskSpace/10000) - 
                       (a.targetconfig_rds.shape_max_vcpu-a.VCPU)*1000000 + (a.VCPU*10000 + a.Memory + a.DiskSpace/10000);
            });
          
            const currentGroup = [remainingDbs.shift()];

            // const remainingDbs2 = [...remainingDbs];

            if (prefs.rdsConsolidation_algo=="Y3") {
              // sort remainingDbs by smallest first
              remainingDbs.sort((a, b) => {
                  return  (a.targetconfig_rds.shape_max_vcpu*10000 + a.Memory + a.DiskSpace/10000)-( b.targetconfig_rds.shape_max_vcpu*10000 + b.Memory + b.DiskSpace/10000)  ;
    
              });
            }    
            
            // Main consolidation loop:
            // • Takes the first remaining database as the start of a new group
            // • Tries to add up to 2 more databases (max group size of 3) from the remaining list
            // • For each potential addition, creates a test group and checks if consolidation is viable using canConsolidate()
            // • If consolidation works, adds the database to the current group and removes it from remaining databases
            
//            for (let i = remainingDbs.length - 1; i >= 0; i--) {
            var runningPDBcount = ( currentGroup[0].COUNT_PDBS==0 ? 1 : currentGroup[0].COUNT_PDBS );
            
            for (let i = 0; i <= (remainingDbs.length - 1);  i++) {
//                if (currentGroup.length < prefs.rdsConsolidation_maxPDBcount*1) {
                if (runningPDBcount + (remainingDbs[i].COUNT_PDBS==0 ? 1 : remainingDbs[i].COUNT_PDBS) <= prefs.rdsConsolidation_maxPDBcount*1) {
                    const testGroup = [...currentGroup, remainingDbs[i]];
                    if (firstN>0) {
                      console.log("checking: ", testGroup.map(item => item.row).join(', '));
                      firstN--;
                    }
                    combinationsChecked++;
                    if (canConsolidate(testGroup, regionInfo, prefs, maxStorage, (firstN>0) )) {
                        runningPDBcount += remainingDbs[i].COUNT_PDBS==0 ? 1 : remainingDbs[i].COUNT_PDBS;
                        currentGroup.push(remainingDbs.splice(i, 1)[0]);
                        if (firstN>0) {
                          console.log("match: ", currentGroup.map(item => item.row).join(', '));
                          
                        }
                    }
                }
            }
            
            consolidatedServers.push(currentGroup);
        }
    });
    
    console.log("Combinations checked:", combinationsChecked);
    
    return consolidatedServers;
}


function getConsolidatedServerTotals(dbGroup) {
    const totalVcpu = dbGroup.reduce((sum, db) => sum + db.VCPU, 0).toFixed(1);
    const totalMemory = dbGroup.reduce((sum, db) => sum + db.Memory, 0);
    const totalThroughput = dbGroup.reduce((sum, db) => sum + db.Throughput, 0);
    const totalIops = dbGroup.reduce((sum, db) => sum + db.IOPS, 0);
    const totalDiskspace = dbGroup.reduce((sum, db) => sum + db.DiskSpace, 0);
    const totalPDBs = dbGroup.reduce((sum, db) => sum + ((db.COUNT_PDBS==0) ? 1 : db.COUNT_PDBS), 0);
    

    return {
        totalVcpu, totalMemory, totalThroughput, totalIops, totalDiskspace, totalPDBs
    };
}





/**
 * Optimizes database consolidation using only RDS-Oracle bare metal shapes.
 * Uses a greedy algorithm to minimize total VCPU usage by starting with an empty
 * bare metal shape and adding databases until limits are reached.
 * 
 * @param {Array} databases - Array of database objects to consolidate
 * @param {Object} regionInfo - AWS region information for shape availability
 * @param {Object} prefs - User preferences for consolidation behavior
 * @returns {Array} Array of database groups, where each group can be consolidated
 */
function optimizeBaremetalConsolidation(databases, regionInfo, prefs) {
    // Get preferred shape from preferences, default to x2iezn.metal
    const preferredShape = prefs.rdsConsolidation_baremetalShape || 'x2iezn.metal';

    var maxStorage = 64000;
    if ( (prefs.rds_algo!='custom') && (prefs.asv_min_storage.trim()!='') && (prefs.asv_min_storage*1>0) ) {
        maxStorage = 256000;
    }
    
    // Get available bare metal shapes, prioritizing the preferred shape
    const baremetalShapes = regionInfo.shapes
        .filter(shape => shape.Name.includes('.metal') && shape.OracleOD > 0)
        .sort((a, b) => {
            // Prioritize preferred shape first, then sort by VCPU
            if (a.Name.includes(preferredShape) && !b.Name.includes(preferredShape)) return -1;
            if (!a.Name.includes(preferredShape) && b.Name.includes(preferredShape)) return 1;
            return a.VCPU - b.VCPU;
        });
    
    if (baremetalShapes.length === 0) {
        console.log("No bare metal shapes available in this region");
        return databases.map(db => [db]); // Return individual databases if no bare metal available
    }
    
    // Get preferred shape VCPU limit for comparison
    const preferredShapeVcpu = baremetalShapes.find(shape => shape.Name.includes(preferredShape))?.VCPU || 48;
    
    // Group databases by MultiAZ and optionally by version
    const multiAzGroups = {};
    databases.forEach(db => {
        let groupKey = db.UseMAZ;
        if (prefs.rdsConsolidation_groupbyversion === "Y") {
            groupKey = `${db.UseMAZ}_${db.VERSION}`;
        }
        
        // Add read replica match grouping if readreplica_algo is Y or auto
        if ((prefs.readreplica_algo === "Y" || prefs.readreplica_algo === "auto") && 
            prefs.readreplica_db_name_match_expr && 
            prefs.readreplica_db_name_match_expr.trim() !== '') {
            
            let matches = false;
            try {
                let pattern = prefs.readreplica_db_name_match_expr.trim();
                if (pattern.includes('*') && !pattern.includes('.*')) {
                    pattern = pattern.replace(/\*/g, '.*');
                }
                const regex = new RegExp(pattern);
                matches = regex.test(db.database);
            } catch (e) {
                matches = false;
            }
            groupKey = `${groupKey}_replica_${matches}`;
        }
        
        if (!multiAzGroups[groupKey]) {
            multiAzGroups[groupKey] = [];
        }
        multiAzGroups[groupKey].push(db);
    });
    
    const consolidatedServers = [];
    
    Object.values(multiAzGroups).forEach(dbList => {
        // Sort databases by VCPU (smallest first for efficient packing)
        dbList.sort((a, b) => a.VCPU - b.VCPU);
        
        const remainingDbs = [...dbList];
        
        while (remainingDbs.length > 0) {
            const currentGroup = [];
            let currentVcpu = 0;
            let currentMemory = 0;
            let currentThroughput = 0;
            let currentIops = 0;
            let currentDiskspace = 0;
            let currentPDBs = 0;
            
            // Try to add databases to current group until we hit a limit
            for (let i = remainingDbs.length - 1; i >= 0; i--) {
                const db = remainingDbs[i];
                const newVcpu = currentVcpu + db.VCPU;
                const newMemory = currentMemory + db.Memory;
                const newThroughput = currentThroughput + db.Throughput;
                const newIops = currentIops + db.IOPS;
                const newDiskspace = currentDiskspace + db.DiskSpace;
                const newPDBs = currentPDBs + (db.COUNT_PDBS === 0 ? 1 : db.COUNT_PDBS);
                
                // Check if adding this database would exceed limits
                if (newPDBs > prefs.rdsConsolidation_maxPDBcount ||
                    newDiskspace > maxStorage) {
                    continue;
                }
                
                // Find suitable shape - prefer user's selected shape unless single DB exceeds its capacity
                let suitableShape;
                if (db.VCPU > preferredShapeVcpu) {
                    // Single DB exceeds preferred shape capacity, use any suitable bare metal
                    suitableShape = baremetalShapes.find(shape => 
                        newVcpu <= shape.VCPU &&
                        newMemory <= shape.Memory &&
                        newThroughput <= shape.Throughput &&
                        newIops <= shape.IOPS
                    );
                } else {
                    // Try preferred shape only
                    suitableShape = baremetalShapes.find(shape => 
                        shape.Name.includes(preferredShape) &&
                        newVcpu <= shape.VCPU &&
                        newMemory <= shape.Memory &&
                        newThroughput <= shape.Throughput &&
                        newIops <= shape.IOPS
                    );
                }
                
                if (suitableShape) {
                    // Add this database to the current group
                    currentGroup.push(remainingDbs.splice(i, 1)[0]);
                    currentVcpu = newVcpu;
                    currentMemory = newMemory;
                    currentThroughput = newThroughput;
                    currentIops = newIops;
                    currentDiskspace = newDiskspace;
                    currentPDBs = newPDBs;
                }
            }
            
            // If we couldn't add any database to an empty group, add the first one individually
            if (currentGroup.length === 0 && remainingDbs.length > 0) {
                currentGroup.push(remainingDbs.shift());
            }
            
            consolidatedServers.push(currentGroup);
        }
    });
    
    return consolidatedServers;
}

/**
 * Determines if a group of databases can be consolidated onto a bare metal RDS instance.
 * 
 * @param {Array} dbGroup - Array of database objects to potentially consolidate
 * @param {Object} regionInfo - AWS region information for shape availability
 * @param {Object} prefs - User preferences including consolidation algorithm
 * @param {number} maxDiskspace - Maximum allowed disk space in GB (default: 64000)
 * @returns {boolean} True if databases can be consolidated, false otherwise
 */
function canConsolidateBaremetal(dbGroup, regionInfo, prefs, maxDiskspace = 64000) {
    // Check MultiAZ consistency
    const multiAzValues = new Set(dbGroup.map(db => db.UseMAZ));
    if (multiAzValues.size > 1) return false;
    
    // Calculate totals
    const totalVcpu = dbGroup.reduce((sum, db) => sum + db.VCPU, 0);
    const totalMemory = dbGroup.reduce((sum, db) => sum + db.Memory, 0);
    const totalThroughput = dbGroup.reduce((sum, db) => sum + db.Throughput, 0);
    const totalIops = dbGroup.reduce((sum, db) => sum + db.IOPS, 0);
    const totalDiskspace = dbGroup.reduce((sum, db) => sum + db.DiskSpace, 0);
    const totalPDBs = dbGroup.reduce((sum, db) => sum + (db.COUNT_PDBS === 0 ? 1 : db.COUNT_PDBS), 0);
    
    // Check basic limits
    if (totalDiskspace > maxDiskspace || totalPDBs > prefs.rdsConsolidation_maxPDBcount) {
        return false;
    }
    
    // Get preferred shape from preferences
    const preferredShape = prefs.rdsConsolidation_baremetalShape || 'x2iezn.metal';
    
    // Get bare metal shapes
    const baremetalShapes = regionInfo.shapes
        .filter(shape => shape.Name.includes('.metal') && shape.OracleOD > 0);
    
    // Get preferred shape VCPU limit
    const preferredShapeVcpu = baremetalShapes.find(shape => shape.Name.includes(preferredShape))?.VCPU || 48;
    
    // Check if any single DB exceeds preferred shape capacity
    const hasLargeDb = dbGroup.some(db => db.VCPU > preferredShapeVcpu);
    
    let suitableShape;
    if (!hasLargeDb) {
        // Try preferred shape first
        suitableShape = baremetalShapes.find(shape => 
            shape.Name.includes(preferredShape) &&
            totalVcpu <= shape.VCPU &&
            totalMemory <= shape.Memory &&
            totalThroughput <= shape.Throughput &&
            totalIops <= shape.IOPS
        );
    }
    
    // Fall back to any suitable bare metal shape if preferred shape doesn't work
    if (!suitableShape) {
        suitableShape = baremetalShapes.find(shape => 
            totalVcpu <= shape.VCPU &&
            totalMemory <= shape.Memory &&
            totalThroughput <= shape.Throughput &&
            totalIops <= shape.IOPS
        );
    }
    
    return !!suitableShape;
}









// this is the main entry point

function   calculate_consolidated_config(regionInfo, databases, DRregionInfo) {
  var servers = [
   ];
  
  
  //clean up strings into numbers
    databases.forEach(item => {
        item.targetconfig_rds.shape_max_vcpu = parseFloat(item.targetconfig_rds.shape_max_vcpu);
        item.targetconfig_rds.shape_memory = parseFloat(item.targetconfig_rds.shape_memory);
        item.targetconfig_rds.shape_throughput = parseFloat(item.targetconfig_rds.shape_throughput);
        item.targetconfig_rds.shape_IOPS = parseFloat(item.targetconfig_rds.shape_IOPS);
        item.VCPU = parseFloat(item.VCPU);
        item.Memory = parseFloat(item.Memory);
        item.Throughput = parseFloat(item.Throughput);
        item.IOPS = parseFloat(item.IOPS);
        item.DiskSpace = parseFloat(item.DiskSpace);
        item.COUNT_PDBS = parseFloat(item.COUNT_PDBS);
        // if (item.COUNT_PDBS==0) {
        //   item.COUNT_PDBS=1;
        // }
        // console.log("item.COUNT_PDBS", item.COUNT_PDBS);
    });
  

// loop through by scenario
// also use this to filter out databases

  // get a list of distinct scenarioNames
  var scenarioNames = new Set();
  for (var dbrow=0; dbrow< databases.length ; dbrow++) {
      // only worry about scenarios where rdsConsolidation_algo!=N
      if (databases[dbrow].frozen_prefs.rdsConsolidation_algo!="N") {
          scenarioNames.add(databases[dbrow].scenarioName);
      }      
  }      
  
  
  var scenarios = [...scenarioNames];
  for (var scen=0; scen< scenarios.length; scen++) {
  
      var scenarioDatabases = [];     
      // Todo: this could be a place where we do a check to avoid counting both CDB and child PDB together
      // downside of doing it here is that we would not be doing similar logic on the RDS side.  So, totals for RDS would include both PDB and CDB.
      // Might be better to find a way to avoid double-counting CDB and child PDB upstream so that both RDS and Exadata calcs are comparable
      
      // need to group by scenarioName as we do this
      for (var groupi=0; groupi< databases.length ; groupi++) {
        if (databases[groupi].scenarioName == scenarios[scen]) {  
            
          // add any unique databases[groupi].assumptions[] to assumptions
            
          //do we include it in the totals?
          if ( ( databases[groupi].PDBandCDBmatch==undefined ) ||
            ( (databases[groupi].frozen_prefs.pdb_and_cdb_exclude_algo == "none") || 
              ( (databases[groupi].frozen_prefs.pdb_and_cdb_exclude_algo == "excludePDB") && (databases[groupi].PDBandCDBmatch!="PDBwithParent") ) || 
              ( (databases[groupi].frozen_prefs.pdb_and_cdb_exclude_algo == "excludeCDB") && (databases[groupi].PDBandCDBmatch!="CDBwithChild") )
            )
          ) {
            
            // skip if the database isnt sized (shape_max_vcpu==0) or if it is a read replica
            if ( (databases[groupi].targetconfig_rds.shape_max_vcpu >0 ) &&
                 ( (databases[groupi].ReadReplica==undefined) || (databases[groupi].ReadReplica=='N') )
              ) {
              scenarioDatabases.push(databases[groupi]);
            }  else {
              console.log("skipping",databases[groupi].row);
            }


          } else {
             //  console.log("excluded database from infrastructure", groupi, databases[groupi].PDBandCDBmatch);
          }    
        }     
      } // end forloop for databases for a given scenario
      



    
    console.log(`Found ${scenarioDatabases.length} databases to consolidate`);
    // console.log(scenarioDatabases[0]);
    
    if (scenarioDatabases.length > 0) {
    
      const originalTotalVcpu = scenarioDatabases.reduce((sum, db) => sum + db.targetconfig_rds.shape_max_vcpu, 0);
      console.log(`Original total VCPUs across all servers: ${originalTotalVcpu}`);
      
      console.log('\nOptimizing database consolidation...');
      const prefs = scenarioDatabases[0].frozen_prefs;
      
      let consolidatedServers;
      if (prefs.rdsConsolidation_algo === "baremetal") {
        console.log('Using bare metal consolidation algorithm...');
        consolidatedServers = optimizeBaremetalConsolidation(scenarioDatabases, regionInfo, prefs);
      } else {
        consolidatedServers = optimizeConsolidation(scenarioDatabases, regionInfo, prefs);
      }
      
      // const newTotalVcpu = consolidatedServers.reduce((sum, serverGroup) => {
      //     return sum + Math.max(...serverGroup.map(db => db.targetconfig_rds.shape_max_vcpu));
      // }, 0);
      // don't use this because we need to repick the rds shape
      
      console.log('\nConsolidation Results:');
      console.log(`Original number of servers: ${scenarioDatabases.length}`);
      console.log(`Consolidated number of servers: ${consolidatedServers.length}`);
      console.log(`Original total VCPUs: ${originalTotalVcpu}`);
      // console.log(`Consolidated total VCPUs: ${newTotalVcpu}`);
      // console.log(`VCPU reduction: ${originalTotalVcpu - newTotalVcpu} (${((originalTotalVcpu - newTotalVcpu) / originalTotalVcpu * 100).toFixed(1)}%)`);
      
  
      // console.log("consolidatedServers", consolidatedServers);
      var row=1;
      
      // console.log('\nConsolidation Summary:');
      consolidatedServers.forEach((serverGroup, i) => {
          const specs = getConsolidatedServerTotals(serverGroup);
          // console.log("specs", specs);
          const dbNames = serverGroup.map(db => db.database);
          const dbNamesLong = serverGroup.map(db => db.database + "(row " + db.row + ", id " + db.collectionID + ", "
               + db.targetconfig_rds.shape
               + ((db.COUNT_PDBS*1>1) ? ", pdbs " + db.COUNT_PDBS : "")
               + ")");
          // console.log("dbNamesLong", dbNamesLong, dbNames);     
          const dbDetails = serverGroup.map(db => ({database: db.database,
               row: db.row,
               shape: db.targetconfig_rds.shape,
               target_vcpu: db.VCPU,
               shape_max_vcpu: db.targetconfig_rds.shape_max_vcpu ,
               hostname: db.hostname,
               description: db.description,
               COUNT_PDBS: db.COUNT_PDBS,
               collectionID: db.collectionID
             }) );
          const originalServerTotalVcpu = serverGroup.reduce((sum, db) => sum + db.targetconfig_rds.shape_max_vcpu, 0);
          const originalMonthlyEstimate = serverGroup.reduce((sum, db) => sum + db.targetconfig_rds.monthly_estimate, 0);
  
          // console.log(`Server ${String(i + 1).padStart(3, '0')}: ${serverGroup.length} databases - ${dbNames.slice(0, 3).join(', ')}`);
          if (dbNames.length > 3) {
              // console.log(`          + ${dbNames.length - 3} more database(s)`);
          }
          
  
          // now pick shape and configure
          var config = {
              VCPU: specs.totalVcpu,
              Memory: specs.totalMemory,
              Throughput: specs.totalThroughput,
              IOPS: specs.totalIops,
              DiskSpace: specs.totalDiskspace,
              rds_type: serverGroup[0].rds_type,
              optgoal_choice: serverGroup[0].optgoal_choice,
              UseRI: serverGroup[0].UseRI,
              UseMAZ: serverGroup[0].UseMAZ,
              frozen_prefs: serverGroup[0].frozen_prefs,
              scenarioName: serverGroup[0].scenarioName,
              assumptionsString: serverGroup[0].assumptionsString,
              manual: serverGroup[0].manual
              
          };    
          
          // Force bare metal shape selection if using baremetal algorithm
          if (prefs.rdsConsolidation_algo === "baremetal") {
              // Temporarily override the bare metal ranking to force bare metal selection
              config.frozen_prefs = {...config.frozen_prefs, rds_baremetal_ranking: "always"};
          }
          
          var rds_shape = pick_rds_shape(regionInfo, config);
          
          var rds_configuration = calculate_rds_config(regionInfo, rds_shape, config);
          
  
          var consolidationString = `${serverGroup.length} Instances consolidated to 1 server.  Provisioned VCPU reduced from ${(originalServerTotalVcpu*1).toFixed(0)} to ${(rds_configuration.targetconfig_rds.shape_max_vcpu*1).toFixed(0)}.`;
          
          if (serverGroup[0].UseMAZ=='Yes') {
            consolidationString = `${serverGroup.length} Instances consolidated to 1 server.  Provisioned VCPU reduced from ${(originalServerTotalVcpu*2).toFixed(0)} to ${(rds_configuration.targetconfig_rds.shape_max_vcpu*2).toFixed(0)} (accounts for MultiAZ).`;
          }
  
          // if we only have 1 database, then we didn't consolidate
          if (serverGroup.length<=1) {
            consolidationString="";
          }
          
          rds_configuration.row = (scen+1) + "." + String(i + 1).padStart(3, '0');
          rds_configuration.server = "Server-" + (scen+1) + "." + String(i + 1).padStart(3, '0');
          rds_configuration.databases = dbNames.join(', ');
          rds_configuration.databasesLong = dbNamesLong.join(', ');
          rds_configuration.orig_VCPU_shape=originalServerTotalVcpu;
          rds_configuration.orig_monthly_estimate=originalMonthlyEstimate;
          rds_configuration.consolidationString=consolidationString;
          rds_configuration.COUNT_PDBS = specs.totalPDBs;
          rds_configuration.databasesArray = dbDetails;
  
  
          servers.push(rds_configuration);
          
          // now add a readreplica if needed
          if (prefs.readreplica_algo == "Y") {
              
              // Check if any database in the group matches the replica name filter
              let shouldCreateReplica = false;
              if (prefs.readreplica_db_name_match_expr && prefs.readreplica_db_name_match_expr.trim() !== '') {
                  try {
                      let pattern = prefs.readreplica_db_name_match_expr.trim();
                      if (pattern.includes('*') && !pattern.includes('.*')) {
                          pattern = pattern.replace(/\*/g, '.*');
                      }
                      const regex = new RegExp(pattern);
                      shouldCreateReplica = serverGroup.some(db => regex.test(db.database));
                  } catch (e) {
                      shouldCreateReplica = false;
                  }
              } else {
                  shouldCreateReplica = true; // No filter means all databases get replicas
              }
              
              if (shouldCreateReplica) {
              
              
              var config2 = structuredClone(config);

              // Apply MultiAZ algorithm for replica
              if (prefs.readreplica_maz_algo === 'single') {
                  config2.UseMAZ = 'No';
              } else if (prefs.readreplica_maz_algo === 'match') {
                  config2.UseMAZ = config.UseMAZ;
              }

             if (prefs.readreplica_size_algo === 'rr50') {
                config2.VCPU = Math.ceil(10*config.VCPU * 0.5)/10;
                config2.Memory = Math.ceil(config.Memory * 0.5);
                config2.Throughput = Math.ceil(config.Throughput * 0.5);
                config2.IOPS = Math.ceil(config.IOPS * 0.5);
              }
              
              if (prefs.readreplica_size_algo === 'rr25') {
                config2.VCPU = Math.ceil(10*config.VCPU * 0.25)/10;
                config2.Memory = Math.ceil(config.Memory * 0.25);
                config2.Throughput = Math.ceil(config.Throughput * 0.25);
                config2.IOPS = Math.ceil(config.IOPS * 0.25);
              }

              //check to make sure the DRregion is the one we got sent
              if (DRregionInfo.region != prefs.readreplica_region) {
                console.log("Consolidation- DRregionInfo.region does not match readreplica_region.");
                config2.frozen_prefs.readreplica_region=DRregionInfo.region;
              }                

  
              var rds_shape2 = pick_rds_shape(DRregionInfo, config2);
          
              var rds_configuration2 = calculate_rds_config(DRregionInfo, rds_shape2, config2);

              rds_configuration2.row = (scen+1) + "." + String(i + 1).padStart(3, '0')+"-R";
              rds_configuration2.server = "Server-" + (scen+1) + "." + String(i + 1).padStart(3, '0')+"-R";
              rds_configuration2.databases = dbNames.join(', ');
              rds_configuration2.databasesLong = dbNamesLong.join(', ');
              rds_configuration2.orig_VCPU_shape=originalServerTotalVcpu;
              rds_configuration2.orig_monthly_estimate=originalMonthlyEstimate;
              rds_configuration2.consolidationString="Replica for " + "Server-" + (scen+1) + "." + String(i + 1).padStart(3, '0');
              rds_configuration2.COUNT_PDBS = specs.totalPDBs;
              rds_configuration2.databasesArray = dbDetails;
              rds_configuration2.ReadReplica='Y';
              rds_configuration2.ReadReplicaRegion=DRregionInfo.region;
              servers.push(rds_configuration2);
              }
                
          }      
          row++;
      });
      
      // could addup servers to get new VCPU count here if we want
     // const newTotalVcpu = servers.reduce((sum, server) => {
      //     return sum + server.targetconfig_rds.shape_max_vcpu;
      // }, 0);
      // 
       
  

    }


  }

// end
  
  return {
    summary: [
        { scenarioName: "notUsedYet",
          beforeServers: 18,
          afterServers: 12,
          beforeVCPU: 100,
          afterVCPU: 88,
          beforeCost: 2456.33,
          afterCost: 2235.33,
          unmappedDatabases: 2
        }  
      ],
    servers: servers
  };
}






export {calculate_consolidated_config};
